import { Button, Checkbox } from 'antd';
import classNames from 'classnames';
import { useContext, useEffect, useState } from 'react';
import { useStore } from 'zustand';

import { Select } from 'app/components/fields/Select';
import { LegacyHotelContext } from 'app/context/HotelContext/LegacyHotelContext';

import { inventoryStore } from '../../../../InventoryStore';

import './PackageSelector.scss';

type PackageSelectorFooterProps = {
  handleSelectAll: () => void;
  allSelect: boolean;
  closeDropdown: () => void;
};

export const PackageSelectorFooter = ({
  handleSelectAll,
  allSelect,
  closeDropdown,
}: PackageSelectorFooterProps) => {
  return (
    <div className="package-selector-footer">
      <Button
        type="link"
        className="select-all-button"
        onClick={handleSelectAll}
        disabled={allSelect}
        size="small"
      >
        Select all
      </Button>
      <Button type="primary" size="small" onClick={closeDropdown}>
        OK
      </Button>
    </div>
  );
};

type PackageSelectorDropDownProps = {
  options: {
    label: string;
    value: number;
  }[];
  handleCheckboxChange: (value: string | number) => void;
  selectedItems: (string | number)[];
  handleSelectAll: () => void;
  allSelect: boolean;
  closeDropdown: () => void;
};

export const PackageSelectorDropDown = ({
  options,
  handleCheckboxChange,
  selectedItems,
  handleSelectAll,
  allSelect,
  closeDropdown,
}: PackageSelectorDropDownProps) => {
  return (
    <div className="package-selector-dropdown-content">
      <div className="package-selector-dropdown-list">
        {options.map((option) => (
          <div
            key={option.value}
            className={classNames([
              'package-selector-item',
              { selected: selectedItems.includes(option.value) },
            ])}
          >
            <Checkbox
              onChange={() => handleCheckboxChange(option.value)}
              checked={selectedItems.includes(option.value)}
            >
              <span className="checkbox-label">{option.label}</span>
            </Checkbox>
          </div>
        ))}
      </div>
      <PackageSelectorFooter
        handleSelectAll={handleSelectAll}
        allSelect={allSelect}
        closeDropdown={closeDropdown}
      />
    </div>
  );
};

type PackageSelectorProps = {
  startTransition: React.TransitionStartFunction;
};

export const PackageSelector = ({ startTransition }: PackageSelectorProps) => {
  const { publishedPackages } = useContext(LegacyHotelContext);
  const selectPackage = useStore(
    inventoryStore,
    (state) => state.selectPackage
  );
  const setSelectPackage = useStore(
    inventoryStore,
    (state) => state.setSelectPackage
  );

  const [allChecked, setAllChecked] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setAllChecked(selectPackage.length === publishedPackages.length);
  }, [selectPackage.length, publishedPackages]);

  const options =
    publishedPackages.map((pkg) => {
      const label =
        pkg.aliasForHotel.trim() ||
        pkg.coverLabel.trim() ||
        `Package #${pkg.id}`;

      return {
        label: label,
        value: pkg.id,
      };
    }) ?? [];

  const handleCheckboxChange = (value: string | number) => {
    startTransition(() => {
      if (typeof value === 'number') {
        const newSelectedItems = selectPackage.includes(value)
          ? selectPackage.filter((item) => item !== value)
          : [...selectPackage, value];

        setSelectPackage(newSelectedItems);
      }
    });
  };

  const handleSelectAll = () => {
    startTransition(() => {
      setSelectPackage(options.map((option) => option.value) ?? []);
    });
  };

  const renderSelectContent = () => {
    if (allChecked) {
      return 'All published packages';
    }

    if (selectPackage.length === 0) {
      return 'Select packages';
    }

    if (selectPackage.length === 1) {
      return publishedPackages.find((pkg) => pkg.id === selectPackage[0])
        ?.aliasForHotel;
    }

    return `${selectPackage.length} packages selected`;
  };

  return (
    <Select
      value={renderSelectContent()}
      showArrow
      popupClassName="package-selector-popup"
      open={open}
      onDropdownVisibleChange={(visible) => setOpen(visible)}
      dropdownRender={() => (
        <PackageSelectorDropDown
          options={options}
          allSelect={allChecked}
          handleCheckboxChange={handleCheckboxChange}
          handleSelectAll={handleSelectAll}
          selectedItems={selectPackage}
          closeDropdown={() => setOpen(false)}
        />
      )}
      className="select-package-action"
      placeholder="Select packages"
      dropdownMatchSelectWidth={false}
    />
  );
};
